import { DOCUMENT } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute, Params } from '@angular/router';
import {
  Subject, catchError, combineLatest, of, take
} from 'rxjs';

import { IconColors, IconSizes, IconUsageTypes } from '@sondermindorg/iris-design-system-angular';
import { ConfigurationService, IProviderShareableLinkAppConfig } from '@sondermind/configuration';
import { GtmCustomEvents, GtmDataLayerService } from '@sondermind/google-tag-manager';
import { SessionType } from '@sondermind/utilities/models-session';
import { WINDOW } from '@sondermind/utilities/tools';

import { IProviderShareableProfile, SpecializedSupport } from '../../shareable-link.interfaces';
import { ShareableLinksHttpService } from '../../shareable-links.httpservice';

interface IRequestSessionParams {
  anonUserId: string;
  referringUrl: string;
  state?: string;
  paymentPref?: string;
  paymentTag?: string;
}

@Component({
  selector: 'landing-page',
  templateUrl: './landing-page.component.html',
  styleUrls: ['./landing-page.component.scss'],
})

export class LandingPageComponent {
  iconColor = IconColors.BRAND1;
  iconSize = IconSizes.MD;
  iconUsage = IconUsageTypes.STAND_ALONE;

  providerShareableProfile: IProviderShareableProfile = {} as IProviderShareableProfile;
  title = 'provider-landing-page';
  isValid: boolean = false;
  experienceSupportingList: string[] = [];
  sessionTypeList: string[] = [];
  isLoading: boolean = true;

  therapyMatchFlowUrl: string = 'https://start.sondermind.com';
  psychiatryMatchFlowUrl: string = 'https://www.sondermind.com/match/psychiatry-flow';
  bookSessionPhoneLink: string = '8669291675';
  bookSessionPhoneDisplay: string = '866-929-1675';

  showInsuranceListLink: boolean = false;
  isBioTruncated: boolean = false;
  providerBio: string = '';
  providerInsurances: string = '';
  noProfilePhoto: boolean = false;
  paramsShareCode: string = '';
  paramsName: string = '';
  credentialStatesFormatted = '';
  requestSessionQueryParams: IRequestSessionParams = null; // For tracking the user coming from .com site

  destroyed$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private shareableLinksHttpService: ShareableLinksHttpService,
    private configurationService: ConfigurationService<IProviderShareableLinkAppConfig>,
    private titleService: Title,
    private metaService: Meta,
    private gtmDataLayerService: GtmDataLayerService,
    @Inject(DOCUMENT) private document: Document,
    @Inject(WINDOW) private window: Window
  ) {}

  ngOnInit(): void {
    combineLatest([this.route.params, this.route.queryParams]).pipe(
      take(1)
    ).subscribe(([params, queryParams]) => {
      this.requestSessionQueryParams = this.handleIncomingQueryParams(params, queryParams);

      this.therapyMatchFlowUrl = this.configurationService.env.app.flowHost;
      this.psychiatryMatchFlowUrl = `${this.configurationService.env.app.flowHost}/launch?launch=psychiatry-flow`;

      this.shareableLinksHttpService.fetch$(params['share_code']).pipe(
        catchError((error) => of(error))
      ).subscribe((response) => {
        if (response.error) {
          this.isLoading = false;
          return;
        }

        this.providerShareableProfile = response.data;

        if (this.providerShareableProfile?.credentialed_states.length > 0) {
          this.formatCredStates();
        }
        this.gtmDataLayerService.dataLayer = {
          event: GtmCustomEvents.PROVIDER_PAGE_VIEW,
          id: this.providerShareableProfile.id,
          full_name: this.providerShareableProfile.full_name
        };

        if (this.providerShareableProfile.profile_photo_url.endsWith('/missing.png')) {
          this.noProfilePhoto = true;
        }

        this.providerInsurances = this.providerShareableProfile.insurances.join(', ');

        if (this.providerShareableProfile.bio?.length > 500) {
          this.truncateBio();
        } else {
          this.providerBio = this.providerShareableProfile.bio;
          this.isBioTruncated = false;
        }

        this.updateMetaInfo(
          this.providerShareableProfile.display_name,
          this.providerShareableProfile.certifications,
          this.providerShareableProfile.bio
        );

        this.updateCanonicalUrl(window.location.href);

        if (this.providerShareableProfile.session_types_offered.includes(SessionType.IN_PERSON)) {
          this.sessionTypeList.push('In-person');
        }
        if (this.providerShareableProfile.session_types_offered.includes(SessionType.VIDEO)) {
          this.sessionTypeList.push(' Video');
        }
        this.experienceSupportingList = this.generateExperienceSupportingList(
          this.providerShareableProfile.ages,
          this.providerShareableProfile.services_offered,
          this.providerShareableProfile.specialties
        );
        this.isValid = true;
        this.isLoading = false;
      });
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private formatCredStates() {
    this.providerShareableProfile?.credentialed_states.forEach((state, i) => {
      if (i !== 0 && i < this.providerShareableProfile?.credentialed_states?.length - 1) {
        this.credentialStatesFormatted += ', ';
      }
      if (this.providerShareableProfile?.credentialed_states?.length > 1
        && i === this.providerShareableProfile?.credentialed_states?.length - 1) {
        this.credentialStatesFormatted += ' and ';
      }
      this.credentialStatesFormatted += state.name;
    });
  }

  scrollToElement(elementId: string): void {
    const element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  }

  private truncateBio(): void {
    // Truncate the bio text to 500 characters and add ellipsis
    this.providerBio = `${this.providerShareableProfile.bio.slice(0, 500)}...`;
    this.isBioTruncated = true;
  }

  toggleBioTruncation(): void {
    // Toggle the bio truncation state when "Read more" is clicked
    if (this.isBioTruncated) {
      this.providerBio = this.providerShareableProfile.bio;
      this.isBioTruncated = false;
    } else {
      this.truncateBio();
      this.scrollToElement('about-section');
    }
  }

  generateExperienceSupportingList(
    ages: string[] = [],
    servicesOffered: string[] = [],
    specialties: Array<{ name: string; description: string; }> = []
  ): string[] {
    const specializedSupportList: string[] = [];
    // The following if statements are used to set order of specialized topics
    if (ages.find((age) => age === SpecializedSupport.INFANTS || age === SpecializedSupport.CHILDREN)) {
      specializedSupportList.push('Children');
    }

    if (ages.includes(SpecializedSupport.ADOLESCENTS)) {
      specializedSupportList.push('Adolescents');
    }

    if (ages.includes(SpecializedSupport.ADULTS)) {
      specializedSupportList.push('Adults');
    }

    if (ages.includes(SpecializedSupport.SENIORS)) {
      specializedSupportList.push('Older adults');
    }

    if (servicesOffered.includes(SpecializedSupport.COUPLES)) {
      specializedSupportList.push('Couples');
    }

    if (specialties.find((spec) => spec.name === SpecializedSupport.LGBTQUIA)) {
      specializedSupportList.push('LGBTQIA+ mental health');
    }

    if (specialties.find((spec) => spec.name === SpecializedSupport.VETERAN)) {
      specializedSupportList.push('Military and veteran mental health');
    }

    if (specialties.find((spec) => spec.name === SpecializedSupport.PERINATAL)) {
      specializedSupportList.push('Perinatal mental health');
    }
    return specializedSupportList;
  }

  private handleIncomingQueryParams(params: Params, queryParams: Params): IRequestSessionParams {
    this.paramsShareCode = params['share_code'];
    this.paramsName = params['name'] ?? '';
    const incomingState: string | null = queryParams['state'] ?? null;
    const incomingPaymentPreference: string | null = queryParams['paymentPref'] ?? null;
    const incomingInsuranceTag: string | null = queryParams['paymentTag'] ?? null;

    let requestSessionQueryParams: IRequestSessionParams = {
      anonUserId: queryParams['anonUserId'],
      referringUrl: encodeURIComponent(window.location.href),
    };
    if (incomingState) {
      requestSessionQueryParams = { ...requestSessionQueryParams, state: incomingState };
    }
    if (incomingPaymentPreference) {
      requestSessionQueryParams = { ...requestSessionQueryParams, paymentPref: incomingPaymentPreference };
    }
    if (incomingInsuranceTag) {
      requestSessionQueryParams = { ...requestSessionQueryParams, paymentTag: incomingInsuranceTag };
    }

    return requestSessionQueryParams;
  }

  private updateMetaInfo(
    providerName: string,
    certifications: Array<{ name: string; abbrev: string; }>,
    bio: string
  ): void {
    const certs: string = certifications?.map((obj) => obj.name).join(', ');
    const title = `${providerName}, ${certs} | SonderMind`;
    // extract the first sentence of the bio;
    const sentence = /[^.!?]*[.!?]/.exec(bio);
    const firstSentenceofBio = sentence ? sentence[0] : bio;
    const first160CharsofBio = firstSentenceofBio.substring(0, 159);
    const description = `${firstSentenceofBio}`;

    this.titleService.setTitle(title);
    this.metaService.updateTag({ name: 'description', content: description });
    this.metaService.updateTag({ property: 'og:url', content: this.window.location.href });
    this.metaService.updateTag({ property: 'og:title', content: title });
    this.metaService.updateTag({ property: 'og:description', content: first160CharsofBio });
    this.metaService.updateTag({ property: 'twitter:url', content: this.window.location.href });
    this.metaService.updateTag({ name: 'twitter:title', content: title });
    this.metaService.updateTag({ name: 'twitter:description', content: first160CharsofBio });
  }

  private updateCanonicalUrl(url: string): void {
    let link: HTMLLinkElement = this.document.querySelector('link[rel="canonical"]');
    if (link) {
      link.href = url;
    } else {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      link.setAttribute('href', url);
      this.document.head.appendChild(link);
    }
  }
}
